import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CurriculumVersionModal } from '../../../../components/Curricula/modals/CurriculumVersionModal';
import { CurriculumVersionsTable } from '../../../../components/Curricula/Tables/CurriculumVersionsTable';
import { Page } from '../../../../components/Page/Page';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useDeleteCurriculumVersionMutation } from '../../../../types/planung-graphql-client-defs';
import { showSuccessDeleteToast, showUserErrorToast } from '../../../../utils/toast';

export const CurriculumVersionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const context = useMemorizedCacheTag('CURRICULUM_VERSIONS');
  const permissions = usePermissionChecker();

  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { confirm, ConfirmationDialog } = useConfirm();

  const [, deleteCurriculumVersion] = useDeleteCurriculumVersionMutation();

  const handleAdd = () => {
    setSelectedUuid(null);
    setModalOpen(true);
  };

  const handleEdit = (uuid: string) => {
    setSelectedUuid(uuid);
    setModalOpen(true);
  };

  const handleDelete = async (uuid: string, name: string) => {
    const result = await confirm({
      message: t('curriculumVersion.deleteConfirm', {
        curriculumVersion: name,
      }),
    });
    if (!result) return;
    setTableLoading(true);
    const { error } = await deleteCurriculumVersion({ where: { uuid } }, context);
    if (error) {
      showUserErrorToast({ error: error });
    } else {
      showSuccessDeleteToast();
    }
    setTableLoading(false);
  };

  return (
    <Page isForbidden={!permissions?.canReadDeputate()} title={t('curriculumVersion.title', { count: 2 })}>
      <CurriculumVersionsTable
        loading={tableLoading}
        onNavigate={(uuid) => navigate(uuid)}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <CurriculumVersionModal selectedUuid={selectedUuid} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <ConfirmationDialog />
    </Page>
  );
};
